import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { films, getFilmById } from "@/mocks/films";
import { recommendFilms } from "@/utils/filmQuery";
import { useFavorites } from "@/hooks/useFavorites";
import FilmCard from "@/components/feature/FilmCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import CastList from "@/pages/detail/components/CastList";
import ShareSheet from "@/pages/detail/components/ShareSheet";

const InfoRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex gap-3 py-2.5 text-sm">
    <span className="w-14 shrink-0 text-foreground-500">{label}</span>
    <span className="min-w-0 flex-1 text-foreground-900">{children}</span>
  </div>
);

export default function Detail() {
  const { id } = useParams();
  const film = getFilmById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [shareOpen, setShareOpen] = useState(false);
  const [favHint, setFavHint] = useState("");

  useEffect(() => {
    if (!film) {
      document.title = "影视作品未找到 - 色即是空";
      return;
    }
    document.title = `${film.title} - 影视详情与剧情介绍 - 色即是空`;
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute(
        "content",
        `${film.title}（${film.year}）由${film.director}执导，${film.cast
          .slice(0, 3)
          .map((member) => member.name)
          .join("、")}主演。${film.tagline}收录完整剧情介绍、演员信息与相关影视推荐。`,
      );
    document
      .querySelector('meta[name="keywords"]')
      ?.setAttribute(
        "content",
        `${film.title},${film.title}剧情,${film.title}演员,${film.genres.join(",")},经典影视`,
      );
  }, [film]);

  if (!film) {
    return (
      <div className="flex min-h-[70vh] w-full items-center justify-center bg-background-50 px-4 pb-16 pt-28">
        <div className="w-full max-w-lg rounded-lg border border-background-200 bg-background-100/60 px-6 py-14 text-center">
          <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-film-line text-2xl" />
          </span>
          <h1 className="font-heading text-xl font-bold text-foreground-950">抱歉，影视作品未找到</h1>
          <p className="mt-2 text-sm leading-relaxed text-foreground-600">
            这条链接可能已经失效，或者资料尚未收录。你可以返回首页，或去分类页继续浏览。
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <Link
              to="/"
              className="rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
            >
              返回首页
            </Link>
            <Link
              to="/category"
              className="rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-800 hover:border-primary-300 hover:text-primary-700"
            >
              浏览影视分类
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const favorite = isFavorite(film.id);
  const related = recommendFilms(film, films, 4);
  const fallbackRelated =
    related.length > 0 ? related : films.filter((item) => item.id !== film.id).slice(0, 4);

  const handleFavorite = () => {
    toggleFavorite(film.id);
    setFavHint(favorite ? "已取消收藏" : "已加入收藏（仅保存在当前浏览器）");
    window.setTimeout(() => setFavHint(""), 2400);
  };

  return (
    <div className="w-full bg-background-50">
      <div className="relative w-full overflow-hidden border-b border-background-200 bg-background-100/60">
        <div className="absolute inset-0">
          <img
            src={film.still}
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover object-top opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-50/70 via-background-50/85 to-background-50" />
        </div>
        <div className="relative mx-auto w-full max-w-[1280px] px-4 pb-8 pt-24 md:px-8 md:pb-10 md:pt-32">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "影视分类", to: "/category" },
              { label: film.genres[0], to: `/category?genre=${encodeURIComponent(film.genres[0])}` },
              { label: film.title },
            ]}
          />

          <div className="mt-6 flex flex-col gap-6 md:flex-row md:gap-9">
            <div className="w-[148px] shrink-0 self-center sm:w-[190px] md:w-[220px] md:self-start">
              <div className="aspect-[2/3] w-full overflow-hidden rounded-lg bg-background-200">
                <img
                  src={film.poster}
                  alt={`${film.title} ${film.year} 电影海报`}
                  title={`${film.title} 影视详情`}
                  className="h-full w-full object-cover"
                />
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                {film.genres.map((genre) => (
                  <Link
                    key={genre}
                    to={`/category?genre=${encodeURIComponent(genre)}`}
                    className="rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] text-secondary-900 transition-colors hover:bg-secondary-200"
                  >
                    {genre}
                  </Link>
                ))}
                <span className="rounded-full bg-accent-100 px-2.5 py-1 text-[11px] text-accent-900">
                  {film.region}
                </span>
              </div>
              <h1 className="mt-3 font-heading text-[28px] font-bold leading-tight text-foreground-950 md:text-[40px]">
                {film.title}
              </h1>
              <p className="mt-1.5 text-sm text-foreground-500">{film.originalTitle}</p>
              <p className="mt-3 max-w-2xl font-heading text-[15px] leading-relaxed text-foreground-700">
                {film.tagline}
              </p>

              <div className="mt-5 flex flex-wrap items-center gap-4">
                <div className="flex items-baseline gap-1.5">
                  <span className="font-heading text-3xl font-bold text-accent-600">
                    {film.rating.toFixed(1)}
                  </span>
                  <span className="text-xs text-foreground-500">/ 10 站内评分</span>
                </div>
                <span className="h-6 w-px bg-background-300" />
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-foreground-600">
                  <span>{film.year} 年</span>
                  <span className="text-background-400">|</span>
                  <span>{film.duration} 分钟</span>
                  <span className="text-background-400">|</span>
                  <span>导演 {film.director}</span>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-2.5">
                <button
                  type="button"
                  onClick={handleFavorite}
                  className={`inline-flex cursor-pointer items-center gap-2 rounded-md px-5 py-2.5 text-sm font-medium transition-colors ${
                    favorite
                      ? "bg-accent-500 text-foreground-950 hover:bg-accent-400"
                      : "bg-primary-500 text-background-50 hover:bg-primary-600"
                  }`}
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className={favorite ? "ri-heart-fill" : "ri-heart-line"} />
                  </span>
                  {favorite ? "已收藏" : "收藏作品"}
                </button>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-share-forward-line" />
                  </span>
                  分享
                </button>
                <Link
                  to="/category"
                  className="inline-flex items-center gap-2 rounded-md px-3 py-2.5 text-sm text-foreground-600 transition-colors hover:text-primary-700"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-arrow-left-line" />
                  </span>
                  返回分类
                </Link>
              </div>
              <p
                className={`mt-2 text-xs text-primary-600 transition-opacity ${
                  favHint ? "opacity-100" : "opacity-0"
                }`}
              >
                {favHint || "占位"}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-8">
            <section id="synopsis">
              <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <span className="flex h-5 w-5 items-center justify-center text-accent-600">
                  <i className="ri-file-text-line" />
                </span>
                剧情简介
              </h2>
              <div className="mt-4 rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-6">
                <p className="text-[15px] leading-[1.95] text-foreground-800">{film.synopsis}</p>
              </div>
            </section>

            <section className="mt-8" id="analysis">
              <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <span className="flex h-5 w-5 items-center justify-center text-accent-600">
                  <i className="ri-quill-pen-line" />
                </span>
                编辑解读
              </h2>
              <div className="mt-4 space-y-3">
                {film.analysis.map((paragraph, index) => (
                  <div
                    key={index}
                    className="flex gap-3.5 rounded-lg border border-background-200/80 bg-background-100/60 p-4 md:p-5"
                  >
                    <span className="font-label text-sm font-bold text-accent-600">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <p className="text-sm leading-[1.9] text-foreground-700">{paragraph}</p>
                  </div>
                ))}
              </div>
            </section>

            <div className="mt-8">
              <CastList cast={film.cast} />
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="rounded-lg border border-background-200/80 bg-background-100/60 p-5">
              <h2 className="mb-2 font-heading text-base font-semibold text-foreground-950">
                基本信息
              </h2>
              <div className="divide-y divide-background-200/70">
                <InfoRow label="导演">{film.director}</InfoRow>
                <InfoRow label="编剧">{film.writers.join(" / ")}</InfoRow>
                <InfoRow label="主演">
                  {film.cast
                    .slice(0, 3)
                    .map((member) => member.name)
                    .join("、")}
                  {film.cast.length > 3 ? " 等" : ""}
                </InfoRow>
                <InfoRow label="年份">
                  {film.year} 年 · {film.region}
                </InfoRow>
                <InfoRow label="类型">{film.genres.join(" / ")}</InfoRow>
                <InfoRow label="片长">{film.duration} 分钟</InfoRow>
                <InfoRow label="站内评分">
                  {film.rating.toFixed(1)} / 10（{film.popularity} 人标记想看）
                </InfoRow>
              </div>
            </div>

            <div className="mt-4 rounded-lg border border-background-200/80 bg-background-50 p-5">
              <h2 className="mb-3 font-heading text-base font-semibold text-foreground-950">资料标签</h2>
              <div className="flex flex-wrap gap-2">
                {film.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?query=${encodeURIComponent(tag)}`}
                    className="rounded-full bg-secondary-100 px-3 py-1.5 text-xs text-secondary-900 transition-colors hover:bg-accent-100 hover:text-accent-900"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
              <div className="mt-4 border-t border-background-200/70 pt-3">
                <p className="flex items-start gap-2 text-[11px] leading-relaxed text-foreground-500">
                  <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                    <i className="ri-information-line" />
                  </span>
                  本站仅提供影视作品的资料整理与剧情介绍，不提供任何影片在线播放或下载。
                </p>
              </div>
            </div>
          </aside>
        </div>

        <section className="mt-12">
          <div className="flex items-end justify-between">
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                相关影视推荐
              </h2>
              <p className="mt-1.5 text-sm text-foreground-500">
                根据类型与标签的相关度推荐，含同导演或同题材作品。
              </p>
            </div>
            <Link
              to={`/category?genre=${encodeURIComponent(film.genres[0])}`}
              className="hidden shrink-0 items-center gap-1 text-sm text-primary-600 hover:text-primary-700 sm:inline-flex"
            >
              更多{film.genres[0]}片
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-arrow-right-line text-xs" />
              </span>
            </Link>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
            {fallbackRelated.map((item) => (
              <FilmCard key={item.id} film={item} />
            ))}
          </div>
        </section>
      </div>

      <ShareSheet open={shareOpen} title={film.title} onClose={() => setShareOpen(false)} />
    </div>
  );
}